import { Dropzone } from '@newfront-insurance/core-ui';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { BLOCKED_FILE_EXTENSIONS } from '@newfront-insurance/files-api';
import { useNotify } from '@newfront-insurance/next-notifications';
import type { JSX } from 'react';
import { useDropzone } from 'react-dropzone';

import type { UploadFile } from '../../types';

interface Props {
  /**
   * Triggered when you select or drop files into the input
   */
  onDrop: (files: UploadFile[]) => void;
  /**
   * Is multi file upload?
   */
  multiple?: boolean;
  /**
   * What are the allowed file types
   */
  accept?: string | string[];
  /**
   * The button label
   */
  label?: string;

  /**
   * The initial file category
   */
  category?: string;

  loading?: boolean;
  /**
   * Custom dropzone component
   */
  customDropzoneComponent?: React.ReactNode;
}

export function FileDropzoneInput({
  onDrop,
  accept,
  multiple = true,
  category = undefined,
  label = 'Upload new files',
  loading,
  customDropzoneComponent,
}: Props): JSX.Element {
  const notify = useNotify();
  const analytics = useAnalytics();
  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept,
    multiple,
    // On Windows, a client with a Network Drive on their Desktop was unable to upload files without getting a
    // 'dash.newfront.com can't open files in this folder because it contains system files'
    // Blocking Dialog that is seemingly a security feature of Chrome's FileSystem Access API
    // The Client used Firefox and it worked fine. They were also able to drag files from their Desktop without issue.
    useFsAccessApi: false,
    onDrop: (files: File[]) => {
      const problematicExtensions = files.filter((file) => {
        return BLOCKED_FILE_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension));
      });

      if (problematicExtensions.length > 0) {
        notify.error(
          `Files ${problematicExtensions.map((file) => file.name).join(', ')} are not allowed because of their extensions`,
        );
        return;
      }

      analytics.track({
        event: 'File added in upload files modal',
        properties: { numberOfFiles: files.length },
      });
      onDrop(
        files.map((file) => ({
          rawFile: file,
          displayName: '',
          summary: '',
          associations: [],
          category,
        })),
      );
    },
    onFileDialogOpen: () => {
      analytics.track({
        event: 'Upload new files clicked to open file dialog',
      });
    },
    // With useFsAccessApi: false, this may not be too reliable
    // See https://github.com/react-dropzone/react-dropzone/tree/master#file-dialog-cancel-callback
    onFileDialogCancel: () => {
      analytics.track({
        event: 'Cancel clicked to close file dialog',
      });
    },
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {customDropzoneComponent || <Dropzone label={label} isLoading={loading} isDragActive={isDragActive} />}
    </div>
  );
}
